/**
 * Settings — a modern SaaS-style account settings page.
 *
 * A left rail navigates seven sections; the active section is mirrored into a
 * `?section=` query param so it is deep-linkable and bookmarkable. On mobile
 * the rail collapses into a horizontally-scrollable tab bar.
 *
 * The whole page is wrapped in a `ToastProvider` so any section can surface
 * feedback without extra wiring.
 */
import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import { DashboardHeader } from '@/src/modules/components/Header';
import { Icons } from '@/src/modules/components/Icons';
import { cn } from '@/src/infrastructure/lib/utils';
import { trackEvent } from '@/src/infrastructure/lib/analytics';

import { ToastProvider } from '@/src/modules/components/settings/ToastProvider';
import { useHasPermission } from '@/src/modules/auth/usePermissions';
import { PartnerProfile } from '@/src/modules/pages/PartnerProfile';
import { ProfileSection } from './sections/ProfileSection';
import { AppPreferencesSection } from './sections/AppPreferencesSection';
import { NotificationSection } from './sections/NotificationSection';
import { SubscriptionSection } from './sections/SubscriptionSection';
import { PrivacySecuritySection } from './sections/PrivacySecuritySection';
import { ConnectedAccountsSection } from './sections/ConnectedAccountsSection';
import { DangerZoneSection } from './sections/DangerZoneSection';

type SectionId =
  | 'profile'
  | 'preferences'
  | 'notifications'
  | 'subscription'
  | 'privacy'
  | 'connected'
  | 'danger'
  | 'partner-profile';

type SettingsSection = {
  id: SectionId;
  label: string;
  icon: any;
  Component: () => any;
  danger?: boolean;
};

const SECTIONS: SettingsSection[] = [
  { id: 'profile', label: 'Profile', icon: Icons.User, Component: ProfileSection },
  {
    id: 'preferences',
    label: 'Application Preferences',
    icon: Icons.Palette,
    Component: AppPreferencesSection,
  },
  {
    id: 'subscription',
    label: 'Subscription & Billing',
    icon: Icons.Subscription,
    Component: SubscriptionSection,
  },
  {
    id: 'privacy',
    label: 'Privacy & Security',
    icon: Icons.Shield,
    Component: PrivacySecuritySection,
  },
  {
    id: 'connected',
    label: 'Connected Accounts',
    icon: Icons.Plug,
    Component: ConnectedAccountsSection,
  },
  {
    id: 'notifications',
    label: 'Notifications',
    icon: Icons.Bell,
    Component: NotificationSection,
  },
  {
    id: 'danger',
    label: 'Danger Zone',
    icon: Icons.Warning,
    Component: DangerZoneSection,
    danger: true,
  },
];

const PARTNER_PROFILE_SECTION: SettingsSection = {
  id: 'partner-profile',
  label: 'Partner Profile',
  icon: Icons.Partner,
  Component: () => <PartnerProfile embedded />,
};

export function Settings({
  defaultSection = 'profile',
  /**
   * When true the page renders only `defaultSection` — no rail, no tabs.
   * Used by the standalone `/dashboard/subscription` route so the Subscription
   * tab is a focused billing view rather than the full settings surface.
   */
  standalone = false,
}: {
  defaultSection?: SectionId;
  standalone?: boolean;
}) {
  const [params, setParams] = useSearchParams();
  const canManagePartnerProfile = useHasPermission('MANAGE_PARTNER_PROFILE');

  React.useEffect(() => {
    trackEvent('SETTINGS_VIEWED', 'navigation');
  }, []);
  const visibleSections = useMemo(
    () => canManagePartnerProfile
      ? [
          SECTIONS[0],
          PARTNER_PROFILE_SECTION,
          ...SECTIONS.slice(1),
        ]
      : SECTIONS,
    [canManagePartnerProfile],
  );

  const active = useMemo<SectionId>(() => {
    const requested = params.get('section') as SectionId | null;
    return visibleSections.some((s) => s.id === requested)
      ? (requested as SectionId)
      : defaultSection;
  }, [params, defaultSection, visibleSections]);

  const activeSection = visibleSections.find((s) => s.id === active) ?? visibleSections[0];
  const ActiveComponent = activeSection.Component;

  function select(id: SectionId) {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('section', id);
      return next;
    });
  }

  // Standalone view — render a single section with its own header and no rail.
  if (standalone) {
    const standaloneSection =
      SECTIONS.find((s) => s.id === defaultSection) ?? SECTIONS[0];
    const StandaloneComponent = standaloneSection.Component;
    const STANDALONE_DESCRIPTIONS: Partial<Record<SectionId, string>> = {
      subscription: 'Manage your plan, usage and payment details.',
      notifications: 'Choose what InviteCloud emails you about.',
    };
    return (
      <ToastProvider>
        <div className="flex-1 flex flex-col min-h-screen bg-surface-lowest pb-20 md:pb-10">
          <DashboardHeader
            title={standaloneSection.label}
            description={STANDALONE_DESCRIPTIONS[standaloneSection.id]}
          />
          <div className="px-4 md:px-8 py-6 w-full max-w-6xl mx-auto">
            <StandaloneComponent />
          </div>
        </div>
      </ToastProvider>
    );
  }

  return (
    <ToastProvider>
      <div className="flex-1 flex flex-col min-h-screen bg-surface-lowest pb-20 md:pb-10">
        <DashboardHeader
          title="Settings"
          description="Manage your account, preferences and subscription."
        />

        <div className="px-4 md:px-8 py-6 w-full max-w-7xl mx-auto">
          {/* Mobile / tablet section tabs */}
          <nav className="lg:hidden -mx-4 px-4 mb-5 flex gap-2 overflow-x-auto pb-1">
            {visibleSections.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => select(s.id)}
                className={cn(
                  'shrink-0 inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-sm font-semibold transition-colors',
                  active === s.id
                    ? s.danger
                      ? 'bg-status-failed text-white'
                      : 'bg-primary text-white'
                    : 'bg-surface-low text-on-surface-variant',
                )}
              >
                <s.icon size={15} />
                {s.label}
              </button>
            ))}
          </nav>

          <div className="flex gap-8">
            {/* Desktop settings rail */}
            <aside className="hidden lg:block w-60 shrink-0">
              <nav className="sticky top-24 flex flex-col gap-1">
                {visibleSections.map((s) => {
                  const isActive = active === s.id;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => select(s.id)}
                      className={cn(
                        'relative flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-semibold transition-colors text-left',
                        isActive
                          ? s.danger
                            ? 'text-status-failed'
                            : 'text-primary'
                          : 'text-on-surface-variant hover:text-on-surface hover:bg-surface-low',
                      )}
                    >
                      {isActive && (
                        <motion.span
                          layoutId="settings-rail-active"
                          transition={{ type: 'spring', stiffness: 400, damping: 32 }}
                          className={cn(
                            'absolute inset-0 rounded-xl',
                            s.danger ? 'bg-status-failed/10' : 'bg-primary/10',
                          )}
                        />
                      )}
                      <s.icon size={17} className="relative shrink-0" />
                      <span className="relative">{s.label}</span>
                    </button>
                  );
                })}
              </nav>
            </aside>

            {/* Active section */}
            <main className="flex-1 min-w-0">
              <AnimatePresence mode="wait">
                <motion.div
                  key={active}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.2 }}
                >
                  <h2 className="text-lg font-bold text-on-surface mb-4 lg:hidden">
                    {activeSection.label}
                  </h2>
                  <ActiveComponent />
                </motion.div>
              </AnimatePresence>
            </main>
          </div>
        </div>
      </div>
    </ToastProvider>
  );
}
